import {createContext, FC, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
// import {useQueryClient} from 'react-query';

import {SplashScreen} from '../../../common/components/layout/SplashScreen';
import {convertNodeToElement, WithChildren} from '../../../common/helpers';
import {getLocalUser, removeLocalUser, setLocalUser} from '../common/Helpers';
import {Enterprise, LoggedInEnterprise, Store, Tokens} from '../common/Types';

type AuthContextProps = {
	currentUser: LoggedInEnterprise | null;
	login: (data: LoggedInEnterprise) => void;
	logout: () => void;
	updateTokens: (tokens: Tokens) => void;
	updateEnterpriseInfo: (enterprise: Enterprise) => void;
	addNewStore: (store: Store) => void;
	isLoggedIn: () => boolean;
	isProfileVerified: boolean;
	isProfileComplete: boolean;
};

const localUser = getLocalUser();

const AuthContext = createContext<AuthContextProps>({
	currentUser: localUser,
	login: (data: LoggedInEnterprise) => {
		console.log(data);
	},
	logout: () => {},
	updateTokens: (tokens: Tokens) => {
		console.log(tokens);
	},
	updateEnterpriseInfo: (enterprise: Enterprise) => {
		console.log(enterprise);
	},
	addNewStore: (store: Store) => {
		console.log(store);
	},
	isLoggedIn: () => false,
	isProfileVerified: false,
	isProfileComplete: false,
});

const useAuth = () => {
	return useContext(AuthContext);
};

const AuthProvider: FC<WithChildren> = ({children}) => {
	const [currentUser, setCurrentUser] = useState<LoggedInEnterprise | null>(getLocalUser());
	const currentUserRef = useRef(currentUser);
	const currentUserInfo = currentUser?.enterpriser;
	// const queryClient = useQueryClient();

	const updateUser = useCallback((user: LoggedInEnterprise) => {
		setCurrentUser(user);
		currentUserRef.current = user;
		setLocalUser(user);
	}, []);

	const login = useCallback(
		(data: LoggedInEnterprise) => {
			const user = {...data};
			updateUser(user);
		},
		[updateUser],
	);
	const logoutWithoutReadyCheck = useCallback(() => {
		setCurrentUser(null);
		removeLocalUser();
	}, []);

	const logout = useCallback(() => {
		if (!currentUser) return;

		logoutWithoutReadyCheck();
		localStorage.clear(); // Or specify the keys if you have other non-user related data
		sessionStorage.clear();
		// removeLocalUser();
	}, [currentUser, logoutWithoutReadyCheck]);

	const updateTokens = useCallback(
		(tokens: Tokens) => {
			if (!currentUser) return;

			updateUser({...currentUser, ...tokens});
		},
		[currentUser, updateUser],
	);

	const updateEnterpriseInfo = useCallback(
		(enterprise: Enterprise) => {
			if (!currentUser) return;

			updateUser({...currentUser, ...{enterpriser: {...currentUser.enterpriser, ...enterprise}}});
		},
		[currentUser, updateUser],
	);

	const addNewStore = useCallback(
		(store: Store) => {
			if (!currentUser || !currentUser.enterpriser || !currentUser.enterpriser.stores) return;

			const newEnterprise = {...currentUser.enterpriser};
			newEnterprise.stores.push(store);
			updateEnterpriseInfo(newEnterprise);
		},
		[currentUser, updateEnterpriseInfo],
	);

	const isProfileVerified = useMemo(() => {
		if (!currentUser) return false;
		if (!currentUser.enterpriser) return false;
		return currentUser.enterpriser.verified;
	}, [currentUser]);

	const isProfileComplete = useMemo(() => {
		if (!isProfileVerified) return false;

		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		return currentUser!.enterpriser!.profileCompleted;
	}, [currentUser, isProfileVerified]);

	const value = useMemo(
		() => ({
			currentUser,
			currentUserInfo,
			login,
			logout,
			updateTokens,
			updateEnterpriseInfo,
			addNewStore,
			isLoggedIn: (): boolean => currentUser !== null,
			isProfileComplete,
			isProfileVerified,
		}),
		[
			currentUser,
			currentUserInfo,
			login,
			logout,
			updateTokens,
			updateEnterpriseInfo,
			addNewStore,
			isProfileComplete,
			isProfileVerified,
		],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const AuthInit: FC<WithChildren> = ({children}) => {
	const {currentUser, login, logout} = useAuth();
	const didRequest = useRef(false);
	const [showSplashScreen, setShowSplashScreen] = useState(true);

	useEffect(() => {
		const requestUser = async (user: LoggedInEnterprise) => {
			try {
				if (!didRequest.current) {
					login(user);
				}
			} catch (error) {
				console.error(error);
				if (!didRequest.current) {
					logout();
				}
			} finally {
				setShowSplashScreen(false);
			}

			return () => {
				didRequest.current = true;
				return didRequest.current;
			};
		};

		if (currentUser) {
			requestUser(currentUser);
		} else {
			logout();
			setShowSplashScreen(false);
		}
		// eslint-disable-next-line
	}, []);

	return showSplashScreen ? <SplashScreen /> : convertNodeToElement(children);
};

export {AuthProvider, AuthInit, useAuth};
